import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";
import { Box, Typography, IconButton, Grid2 } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import {
  BTN_CANCEL,
  BTN_SAVE_PATIENT_DETAILS,
  FORM_LABEL_STATUS,
  LABEL_COMMENT,
  PATIENT_DETAILS,
  PLACEHOLDER_COMMENT,
} from "../../utility/constants/locale";
import PatientDetailsCard from "./PatientDetailsCard";
import { useUpdatePatientDetailsMutation } from "./services/UpdatePatientDetailsAPISlice";
import {
  formatDate,
  getDropdownDataForCategory,
  getPatientStatusTrasitionsOptions,
  segregateCategoryWiseStatus,
} from "../../utility/functions/common-functions";
import CustomDropdown from "../../components/custom-dropdown/CustomDropdown";
import CustomTextField from "../../components/custom-text-field/CustomTextField";
import CustomButton from "../../components/custom-button/CustomButton";
import CustomSpinner from "../../components/custom-spinner/CustomSpinner";
import { useGetPatientDetailsQuery } from "./services/GetPatientDetailAPISlice";
import { enqueueSnackbar } from "notistack";
import {
  CATEGORY_ID_MAPPING,
  EDIT_MANAGE_PATIENTS_MESSAGES,
} from "../../utility/constants/common-constants";
import { usePostPatientStatusTransitionsMutation } from "./services/PostPatientStatusTransitionAPISlice";

const EditPatientDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    statusId: 0,
    adminReasonId: 0,
    comments: "",
  });
  const [patientDetails, setPatientDetails] = useState<any | null>(null);
  const [statusDropDownOptions, setStatusDropdownOptions] = useState<
    { label: string; value: number }[] | undefined
  >();
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [isErrorInTransitions, setIsErrorInTransitions] = useState("");

  const categoryWiseStatus = useAppSelector(
    (state) => state.adminMasterData.categoryWiseStatus
  );

  const statusDropdownData = useMemo(() => {
    return getDropdownDataForCategory(
      segregateCategoryWiseStatus(categoryWiseStatus),
      CATEGORY_ID_MAPPING.patientCase,
      false
    );
  }, [categoryWiseStatus]);

  const accessToken = useAppSelector(
    (state) => state.adminAuth.user?.accessToken!
  );
  const adminEmail = useAppSelector((state) => state.adminAuth.user?.email);

  const {
    data: patientData,
    isLoading: isPatientLoading,
    error,
    refetch,
  } = useGetPatientDetailsQuery({
    accessToken,
    patientCaseId: Number(id),
  });

  const [updatePatientDetails, { isLoading }] =
    useUpdatePatientDetailsMutation();

  const [postPatientStatusTransitions] =
    usePostPatientStatusTransitionsMutation();

  useEffect(() => {
    const fetchStatusTransitions = async () => {
      if (
        patientData &&
        patientData.managePatientsOverview.statusId &&
        statusDropdownData &&
        statusDropdownData.length > 0
      ) {
        setIsDataLoading(true);
        setIsErrorInTransitions("");

        try {
          // Set patient details
          setFormData((prevFormData) => ({
            ...prevFormData,
            statusId: patientData.managePatientsOverview.statusId,
          }));

          setPatientDetails({
            enrollmentDate: formatDate(
              patientData.managePatientsOverview.patientEnrolledDate
            ),
            dueDate: formatDate(
              patientData.managePatientsOverview.initialDeliveryDueDate
            ),
            patientStatus: patientData.managePatientsOverview.statusDisplayName,
            displayGroupDisplayName:
              patientData.managePatientsOverview.displayGroupDisplayName,
            currentTaskProgress:
              patientData.managePatientsOverview.groupCompletionPercentage,
            rewardsTierLevel: patientData.managePatientsOverview.currentTier,
            rewardPoints: patientData.managePatientsOverview.earnedPoints,
            totalRewardPoints:
              patientData.managePatientsOverview.totalRewardPoints,
          });

          // Fetch status transitions based on the current statusId
          const response = await postPatientStatusTransitions({
            body: {
              currentStatus: patientData.managePatientsOverview.statusId,
            },
            accessToken,
          }).unwrap();

          const options = getPatientStatusTrasitionsOptions(
            statusDropdownData,
            response,
            patientData.managePatientsOverview.statusId
          );
          setStatusDropdownOptions(options);
        } catch (error) {
          setIsErrorInTransitions("Error fetching status transitions.");
          setStatusDropdownOptions([]); // Fallback to empty options
        } finally {
          setIsDataLoading(false);
        }
      }
    };

    fetchStatusTransitions();
  }, [patientData, statusDropdownData]);

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (patientData.managePatientsOverview.statusId === formData.statusId) {
      enqueueSnackbar(
        EDIT_MANAGE_PATIENTS_MESSAGES.editManagePatients.SameStatus,
        {
          variant: "warning",
        }
      );
      return;
    }

    const body = {
      patientCaseId: Number(id),
      currentStatus: patientData.managePatientsOverview.statusId,
      targetStatus: formData.statusId,
      comments: formData.comments,
      adminEmail,
    };

    try {
      await updatePatientDetails({ body, accessToken }).unwrap();
      refetch();
      navigate("/admin"); // Redirect after success
      enqueueSnackbar(
        EDIT_MANAGE_PATIENTS_MESSAGES.editManagePatients.Success,
        {
          variant: "success",
        }
      );
    } catch (error) {
      enqueueSnackbar(EDIT_MANAGE_PATIENTS_MESSAGES.editManagePatients.Fail, {
        variant: "error",
      });
    }
  };

  const handleCancel = () => {
    navigate("/admin");
  };

  if (isPatientLoading || isDataLoading || isLoading) return <CustomSpinner />;

  if (error || isErrorInTransitions) {
    return (
      <Box display="flex" justifyContent="center" mt={3}>
        <Typography variant="h6" color="error">
          {error ? "Error loading patient details." : isErrorInTransitions}
        </Typography>
      </Box>
    );
  }

  return (
    <Box padding={"2rem"}>
      <Grid2 container spacing={3}>
        {/* Header Section */}
        <Grid2 size={11} display="flex" alignItems="center">
          <IconButton onClick={handleCancel} aria-label="back button">
            <KeyboardArrowLeftIcon />
          </IconButton>
          <Typography variant="h2">{PATIENT_DETAILS}</Typography>
        </Grid2>

        {/* Patient Name */}
        <Grid2 size={11} ml={3}>
          <Typography variant="h2">
            {patientData.managePatientsOverview.preferredDisplayName}
          </Typography>
        </Grid2>

        {/* Patient Details Card */}
        {patientDetails && (
          <Grid2 size={12}>
            <PatientDetailsCard values={patientDetails} />
          </Grid2>
        )}

        {/* Dropdowns */}
        <Grid2 container size={8}>
          <Grid2 size={4}>
            <CustomDropdown
              inputFormTitle={FORM_LABEL_STATUS}
              value={formData.statusId}
              changeTrigger={(value) => handleInputChange("statusId", value)}
              options={statusDropDownOptions || []}
            />
          </Grid2>
        </Grid2>

        {/* Comment Box */}
        <Grid2 size={11}>
          <CustomTextField
            inputFormTitle={LABEL_COMMENT}
            value={formData.comments}
            changeTrigger={(value) => handleInputChange("comments", value)}
            multi
            rows={2}
            maxLength={512}
            placeholder={PLACEHOLDER_COMMENT}
          />
        </Grid2>

        {/* Buttons */}
        <Grid2 size={12} container spacing={2} justifyContent="flex-start">
          <Grid2>
            <CustomButton
              variant="outlined"
              onClick={handleCancel}
              text={BTN_CANCEL}
            />
          </Grid2>
          <Grid2>
            <CustomButton
              type="submit"
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              text={BTN_SAVE_PATIENT_DETAILS}
            />
          </Grid2>
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default EditPatientDetails;
